/** @fileoverview Stores tenant-scoped Fabrik conversation data and mutation state. */

import { create } from 'zustand';
import {
  deleteConversation,
  deleteFeedback,
  getFeedbackCategories,
  listConversations,
  listMessages,
  renameConversation,
  saveFeedback,
} from '../services/chatApi';
import { ApiError } from '../services/platformAuth';
import type { Conversation, Feedback, Message } from '../types/chat';

type ChatStore = {
  tenantId: string | null;
  conversationAppId: string | null;
  conversations: Conversation[];
  total: number;
  selected: Conversation | null;
  messages: Message[];
  messageTotal: number;
  model: string;
  loading: boolean;
  conversationsLoading: boolean;
  error: string;
  planRequired: 'fabrik' | 'shared-ai' | null;
  quotaExhausted: boolean;
  feedback: Feedback | null;
  categories: string[];
  saving: boolean;
  setTenant: (tenantId: string) => void;
  clearTenant: () => void;
  setConversationAppId: (appId: string | null) => void;
  setModel: (model: string) => void;
  setSelectedConversation: (conversation: Conversation) => void;
  dismissError: () => void;
  handleError: (error: unknown) => void;
  loadConversations: (offset?: number) => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  openConversation: (conversation: Conversation | null) => Promise<boolean>;
  loadMoreMessages: () => Promise<void>;
  renameSelected: (title: string) => Promise<void>;
  deleteSelected: () => Promise<void>;
  rateMessage: (message: Message, rating: string) => Promise<void>;
  setFeedback: (feedback: Feedback | null) => void;
  submitFeedback: (rating: string, category: string, comment: string) => Promise<void>;
};

const initialState = {
  tenantId: null,
  conversationAppId: null,
  conversations: [],
  total: 0,
  selected: null,
  messages: [],
  messageTotal: 0,
  model: 'fast',
  loading: false,
  conversationsLoading: false,
  error: '',
  planRequired: null,
  quotaExhausted: false,
  feedback: null,
  categories: [],
  saving: false,
};

let messageController: AbortController | null = null;
let lifeController = new AbortController();

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong. Please try again.';

export const useChatStore = create<ChatStore>((set, get) => ({
  ...initialState,

  setTenant: (tenantId) => {
    if (get().tenantId === tenantId) return;
    lifeController.abort();
    messageController?.abort();
    lifeController = new AbortController();
    messageController = null;
    set({ ...initialState, tenantId });
  },

  clearTenant: () => {
    lifeController.abort();
    messageController?.abort();
    lifeController = new AbortController();
    messageController = null;
    set(initialState);
  },

  setConversationAppId: (conversationAppId) => {
    const state = get();
    if (state.conversationAppId === conversationAppId) return;
    const selectedMatchesApp =
      !state.selected || (state.selected.app_id ?? null) === conversationAppId;
    if (!selectedMatchesApp) {
      messageController?.abort();
      messageController = null;
    }
    set({
      conversationAppId,
      conversations: [],
      total: 0,
      ...(!selectedMatchesApp
        ? {
            selected: null,
            messages: [],
            messageTotal: 0,
            loading: false,
            feedback: null,
            error: '',
          }
        : {}),
    });
  },

  setModel: (model) => set({ model }),
  setSelectedConversation: (selected) => set({ selected }),
  setFeedback: (feedback) => set({ feedback }),
  dismissError: () => set({ error: '' }),

  handleError: (error) => {
    if (error instanceof Error && error.name === 'AbortError') return;
    const next: Partial<ChatStore> = { error: errorText(error) };
    if (error instanceof ApiError) {
      if (error.code === 'FABRIK_PLAN_REQUIRED') next.planRequired = 'fabrik';
      if (error.code === 'SHARED_AI_PLAN_REQUIRED') next.planRequired = 'shared-ai';
      if (error.code === 'FABRIK_AI_QUOTA_EXHAUSTED') next.quotaExhausted = true;
    }
    set(next);
  },

  loadConversations: async (offset = 0) => {
    const { tenantId, conversationAppId } = get();
    if (!tenantId) return;
    set({ conversationsLoading: true });
    try {
      const data = await listConversations(
        tenantId,
        offset,
        lifeController.signal,
        conversationAppId || undefined
      );
      if (get().conversationAppId !== conversationAppId) return;
      set((state) => ({
        conversations: offset
          ? [...state.conversations, ...data.assistantConversations]
          : data.assistantConversations,
        total: data.totalCount,
      }));
    } finally {
      set({ conversationsLoading: false });
    }
  },

  loadMoreConversations: async () => {
    try {
      await get().loadConversations(get().conversations.length);
    } catch (error) {
      get().handleError(error);
    }
  },

  openConversation: async (conversation) => {
    messageController?.abort();
    const controller = new AbortController();
    messageController = controller;
    const tenantId = get().tenantId;
    set({
      selected: conversation,
      messages: [],
      messageTotal: 0,
      error: '',
      loading: Boolean(conversation),
      feedback: null,
    });
    if (!conversation) {
      set({ loading: false });
      return true;
    }
    if (!tenantId) {
      set({ loading: false });
      return false;
    }
    try {
      const data = await listMessages(tenantId, conversation.id, 0, controller.signal);
      if (controller.signal.aborted) return false;
      set({ messages: [...data.assistantMessages].reverse(), messageTotal: data.totalCount });
      return true;
    } catch (error) {
      if (!controller.signal.aborted) get().handleError(error);
      return false;
    } finally {
      if (!controller.signal.aborted) set({ loading: false });
    }
  },

  loadMoreMessages: async () => {
    const { selected, messages, tenantId } = get();
    if (!selected || !tenantId) return;
    const controller = messageController;
    set({ loading: true });
    try {
      const data = await listMessages(tenantId, selected.id, messages.length, controller?.signal);
      if (!controller?.signal.aborted && get().selected?.id === selected.id) {
        set((state) => ({
          messages: [...data.assistantMessages].reverse().concat(state.messages),
          messageTotal: data.totalCount,
        }));
      }
    } catch (error) {
      if (!controller?.signal.aborted && get().selected?.id === selected.id)
        get().handleError(error);
    } finally {
      if (!controller?.signal.aborted && get().selected?.id === selected.id)
        set({ loading: false });
    }
  },

  renameSelected: async (title) => {
    const { selected, tenantId } = get();
    if (!selected || !tenantId) return;
    try {
      await renameConversation(tenantId, selected.id, title, lifeController.signal);
      set((state) => ({
        selected: state.selected ? { ...state.selected, title } : state.selected,
      }));
      await get().loadConversations();
    } catch (error) {
      get().handleError(error);
    }
  },

  deleteSelected: async () => {
    const { selected, tenantId } = get();
    if (!selected || !tenantId) return;
    try {
      await deleteConversation(tenantId, selected.id, lifeController.signal);
      await get().openConversation(null);
      await get().loadConversations();
    } catch (error) {
      get().handleError(error);
    }
  },

  rateMessage: async (message, rating) => {
    const { selected, tenantId } = get();
    if (!selected || !tenantId) return;
    try {
      if (message.assistant_message_feedback?.rating === rating) {
        await deleteFeedback(tenantId, selected.id, message.id, lifeController.signal);
        set((state) => ({
          messages: state.messages.map((item) =>
            item.id === message.id ? { ...item, assistant_message_feedback: null } : item
          ),
        }));
      } else {
        const categories = await getFeedbackCategories(tenantId, lifeController.signal);
        set({ categories, feedback: { id: message.id, rating } });
      }
    } catch (error) {
      get().handleError(error);
    }
  },

  submitFeedback: async (rating, category, comment) => {
    const { feedback, selected, tenantId } = get();
    if (!feedback || !selected || !tenantId) return;
    set({ saving: true });
    try {
      await saveFeedback(
        tenantId,
        selected.id,
        feedback.id,
        {
          rating,
          ...(category ? { category } : {}),
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
        lifeController.signal
      );
      set((state) => ({
        messages: state.messages.map((item) =>
          item.id === feedback.id ? { ...item, assistant_message_feedback: { rating } } : item
        ),
        feedback: null,
      }));
    } catch (error) {
      get().handleError(error);
    } finally {
      set({ saving: false });
    }
  },
}));

export type { ChatStore };
